import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { toast } from "react-toastify";

// ── Draggable product card ─────────────────────────────────────────────────────
const ProductCard = ({
  product,
  page,
  isDragging,
  onDragStart,
  onDragEnd,
  onMoveToPage,
  pages,
  isMobile,
}) => (
  <Box
    draggable={!isMobile}
    onDragStart={!isMobile ? onDragStart : undefined}
    onDragEnd={!isMobile ? onDragEnd : undefined}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      px: 1,
      py: "6px",
      mb: 0.75,
      borderRadius: "8px",
      border: "1px solid var(--border-color)",
      backgroundColor: "var(--bg-primary)",
      cursor: isMobile ? "default" : "grab",
      opacity: isDragging ? 0.35 : 1,
      transition: "opacity 0.15s",
      userSelect: "none",
      "&:active": { cursor: isMobile ? "default" : "grabbing" },
    }}
  >
    {!isMobile && (
      <DragIndicatorIcon
        sx={{ fontSize: 15, color: "var(--text-tertiary)", flexShrink: 0 }}
      />
    )}
    <Tooltip title={product.name} placement="top">
      <Typography
        noWrap
        sx={{
          flex: 1,
          fontFamily: "Poppins",
          fontSize: 12,
          color: "var(--text-primary)",
          minWidth: 0,
        }}
      >
        {product.name || product._id}
      </Typography>
    </Tooltip>

    {/* Mobile: inline page selector */}
    {isMobile && (
      <Select
        size="small"
        value={
          page === "" || page === null || page === undefined
            ? "unassigned"
            : String(page)
        }
        onChange={(e) =>
          onMoveToPage(
            e.target.value === "unassigned" ? "" : Number(e.target.value),
          )
        }
        sx={{
          fontFamily: "Poppins",
          fontSize: 11,
          ml: 0.5,
          height: 28,
          flexShrink: 0,
          minWidth: 100,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--border-color)",
          },
          "& .MuiSelect-select": {
            py: "2px",
            px: "8px",
            color: "var(--text-primary)",
            backgroundColor: "var(--bg-paper)",
          },
        }}
      >
        <MenuItem
          value="unassigned"
          sx={{ fontFamily: "Poppins", fontSize: 12 }}
        >
          Unassigned
        </MenuItem>
        {pages.map((pg) => (
          <MenuItem
            key={pg}
            value={String(pg)}
            sx={{ fontFamily: "Poppins", fontSize: 12 }}
          >
            Page {pg}
          </MenuItem>
        ))}
      </Select>
    )}
  </Box>
);

// ── Page column ────────────────────────────────────────────────────────────────
const PageColumn = ({
  label,
  products,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  draggingId,
  onDragStart,
  onDragEnd,
  onMoveToPage,
  pages,
  isMobile,
  canDelete,
  onDeletePage,
}) => (
  <Box
    onDragOver={!isMobile ? onDragOver : undefined}
    onDragLeave={!isMobile ? onDragLeave : undefined}
    onDrop={!isMobile ? onDrop : undefined}
    sx={{
      flexShrink: 0,
      width: isMobile ? "100%" : 200,
      backgroundColor: isDragOver ? "rgba(25,118,210,0.06)" : "var(--bg-paper)",
      border: isDragOver ? "2px dashed" : "1px solid",
      borderColor: isDragOver ? "primary.main" : "var(--border-color)",
      borderRadius: "10px",
      p: 1.25,
      transition: "border-color 0.15s, background-color 0.15s",
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
    }}
  >
    {/* Header */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
        flexShrink: 0,
      }}
    >
      <Typography
        fontFamily="Poppins"
        fontWeight={600}
        fontSize={13}
        color="var(--text-primary)"
      >
        {label}
      </Typography>
      {canDelete && (
        <Tooltip title="Remove page (moves products to Unassigned)">
          <IconButton
            size="small"
            onClick={onDeletePage}
            sx={{ p: 0.25, color: "var(--text-tertiary)" }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>

    {/* Product cards */}
    <Box sx={{ flex: 1 }}>
      {products.length === 0 ? (
        <Typography
          fontFamily="Poppins"
          fontSize={11}
          color="var(--text-tertiary)"
          textAlign="center"
          sx={{ pt: 2 }}
        >
          {isMobile ? "No products" : "Drop products here"}
        </Typography>
      ) : (
        products.map((p) => (
          <ProductCard
            key={p.product._id}
            product={p.product}
            page={p.page}
            isDragging={draggingId === p.product._id}
            onDragStart={() => onDragStart(p.product._id)}
            onDragEnd={onDragEnd}
            onMoveToPage={(newPage) => onMoveToPage(p.product._id, newPage)}
            pages={pages}
            isMobile={isMobile}
          />
        ))
      )}
    </Box>
  </Box>
);

// ── Main Modal ─────────────────────────────────────────────────────────────────
const PageBoardModal = ({ open, onClose, products, onApply }) => {
  const isMobile = useMediaQuery("(max-width:899px)");
  const [localProducts, setLocalProducts] = useState([]);
  const [pages, setPages] = useState([1]);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  useEffect(() => {
    if (!open) return;
    const filled = products.filter((p) => p.product);
    setLocalProducts(filled.map((p) => ({ ...p })));
    const nums = [
      ...new Set(
        filled
          .map((p) => p.page)
          .filter(
            (pg) =>
              pg !== "" && pg !== null && pg !== undefined && Number(pg) > 0,
          )
          .map((pg) => Number(pg))
          .filter((n) => !isNaN(n)),
      ),
    ].sort((a, b) => a - b);
    setPages(nums.length > 0 ? nums : [1]);
  }, [open]);

  const getProductsForPage = (pg) =>
    localProducts.filter((p) =>
      pg === "unassigned"
        ? !p.page || p.page === "" || Number(p.page) === 0
        : Number(p.page) === pg,
    );

  const handleDragStart = (productId) => {
    setDraggingId(productId);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOver(null);
  };

  const handleDrop = (targetPage) => {
    if (!draggingId) return;
    const newPage = targetPage === "unassigned" ? "" : targetPage;
    setLocalProducts((prev) =>
      prev.map((p) =>
        p.product._id === draggingId ? { ...p, page: newPage } : p,
      ),
    );
    setDraggingId(null);
    setDragOver(null);
  };

  const handleMoveToPage = (productId, newPage) => {
    setLocalProducts((prev) =>
      prev.map((p) =>
        p.product._id === productId ? { ...p, page: newPage } : p,
      ),
    );
  };

  const handleAddPage = () => {
    const max = pages.length > 0 ? Math.max(...pages) : 0;
    setPages((prev) => [...prev, max + 1]);
  };

  const handleDeletePage = (pg) => {
    setLocalProducts((prev) =>
      prev.map((p) => (Number(p.page) === pg ? { ...p, page: "" } : p)),
    );
    setPages((prev) => prev.filter((p) => p !== pg));
  };

  const handleApply = () => {
    onApply(localProducts);
    toast.success("Page rearrangement applied successfully!");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          backgroundColor: "var(--bg-primary)",
          height: { xs: "95vh", md: "85vh" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.75, flexShrink: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            fontFamily="Poppins"
            fontWeight={600}
            fontSize={15}
            color="var(--text-primary)"
          >
            Page Board
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "var(--text-primary)" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          fontFamily="Poppins"
          fontSize={12}
          color="var(--text-tertiary)"
          sx={{ mt: 0.25 }}
        >
          {isMobile
            ? "Use the dropdown on each product to change its page"
            : "Drag any product card to move it to another page"}
        </Typography>
      </DialogTitle>

      <Divider sx={{ borderColor: "var(--border-color)", flexShrink: 0 }} />

      <DialogContent
        sx={{
          flex: 1,
          overflow: "auto",
          p: { xs: 1.5, md: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 1.5,
            alignItems: "flex-start",
            minHeight: "100%",
            overflowX: isMobile ? undefined : "auto",
            pb: isMobile ? 1 : 0,
          }}
        >
          {/* Unassigned column */}
          <PageColumn
            label="Unassigned"
            products={getProductsForPage("unassigned")}
            isDragOver={dragOver === "unassigned"}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver("unassigned");
            }}
            onDragLeave={() => setDragOver(null)}
            onDrop={() => handleDrop("unassigned")}
            draggingId={draggingId}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onMoveToPage={handleMoveToPage}
            pages={pages}
            isMobile={isMobile}
            canDelete={false}
          />

          {/* Numbered page columns */}
          {pages.map((pg) => (
            <PageColumn
              key={pg}
              label={`Page ${pg}`}
              products={getProductsForPage(pg)}
              isDragOver={dragOver === pg}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(pg);
              }}
              onDragLeave={() => setDragOver(null)}
              onDrop={() => handleDrop(pg)}
              draggingId={draggingId}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onMoveToPage={handleMoveToPage}
              pages={pages}
              isMobile={isMobile}
              canDelete={getProductsForPage(pg).length === 0}
              onDeletePage={() => handleDeletePage(pg)}
            />
          ))}

          {/* New page button */}
          <Box
            sx={{
              flexShrink: 0,
              alignSelf: isMobile ? "stretch" : "flex-start",
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddPage}
              fullWidth={isMobile}
              sx={{
                textTransform: "none",
                fontFamily: "Poppins",
                fontSize: 12,
                whiteSpace: "nowrap",
                borderStyle: "dashed",
                borderColor: "primary.main",
                color: "primary.main",
                px: 1.5,
                py: 0.75,
                borderRadius: "10px",
              }}
            >
              New page
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          pb: 2,
          pt: 1,
          flexShrink: 0,
          borderTop: "1px solid var(--border-color)",
          justifyContent: "space-between",
        }}
      >
        <Typography
          fontFamily="Poppins"
          fontSize={12}
          color="error.main"
          sx={{ mt: 1, fontWeight: 500 }}
        >
          ⚠️ Make sure you apply changes to save the rearrangement, otherwise it
          will revert to the initial state.
        </Typography>
        <Box
          sx={{ flex: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}
        >
          <Button
            onClick={onClose}
            size="small"
            sx={{
              textTransform: "none",
              fontFamily: "Poppins",
              color: "var(--text-primary)",
              borderColor: "var(--border-color)",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            sx={{ textTransform: "none", fontFamily: "Poppins" }}
          >
            Apply Changes
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PageBoardModal;
